/**
 * The panel behind `/messages/requests?dialog=request-preview&chatId=<id>`
 * (R5, R6, R7). It only ever shows the request's preview: the first message,
 * with Approve and Ignore in place of a composer. Once Approve succeeds it
 * hands the chatId to `onApproved`, and the route replaces this screen with
 * the real conversation at `/messages?conversationId=<chatId>`, rendered by
 * `ConversationPane`, per `51-message-requests-and-moderation.md` "Approve"
 * (ISSUE-1050). There is no in-dialog chat.
 *
 * Always mounted (mirrors `InvitePreviewDialog.tsx`); `open` is derived from
 * whether `chatId` is defined, not from a separate boolean. The route keys
 * this component by `chatId`, so each request mounts a fresh panel.
 */
import {
  type useMessageRequestApprove,
  type useMessageRequestDecline,
} from "@sportsmet/messaging-sdk/private";
import { Button, Dialog } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";

export type typeRequestConversationPreview = {
  fromDisplayName: string;
  firstMessagePreview: string;
};

export type typeRequestConversationPanelProps = {
  /** `undefined` means the panel is closed. */
  chatId: string | undefined;
  previewMessage: typeRequestConversationPreview | undefined;
  onClose: () => void;
  approveMutation: ReturnType<typeof useMessageRequestApprove>;
  declineMutation: ReturnType<typeof useMessageRequestDecline>;
  /** Called once with the approved chatId after a successful approve. The
   * route navigates to `/messages?conversationId=<chatId>`. */
  onApproved: (chatId: string) => void;
};

export const RequestConversationPanel = (
  props: typeRequestConversationPanelProps,
): ReactNode => {
  const isOpen = props.chatId !== undefined;

  const handleApprove = async (): Promise<void> => {
    if (props.chatId === undefined) return;
    let response;
    try {
      response = await props.approveMutation.mutateAsync({
        chatId: props.chatId,
      });
    } catch {
      showOfflineError();
      return;
    }
    if (response.error || !response.data?.isApproved) {
      showApiError(response);
      return;
    }
    props.onApproved(props.chatId);
  };

  const handleIgnore = async (): Promise<void> => {
    if (props.chatId === undefined) return;
    let response;
    try {
      response = await props.declineMutation.mutateAsync({
        chatId: props.chatId,
      });
    } catch {
      showOfflineError();
      return;
    }
    if (response.error) {
      showApiError(response);
      return;
    }
    props.onClose();
  };

  const body: ReactNode = props.previewMessage ? (
    <div className="flex flex-col gap-3">
      <p className="text-caption text-ink-secondary">
        {props.previewMessage.fromDisplayName}
      </p>
      <p
        className="max-w-4/5 self-start rounded-md bg-surface-sunken px-3 py-2 text-body text-ink"
        data-testid="request-preview-message"
      >
        {props.previewMessage.firstMessagePreview}
      </p>
    </div>
  ) : null;
  const footer: ReactNode = (
    <>
      <Button
        variant="outline"
        loading={props.declineMutation.isPending}
        loadingLabel="Ignoring"
        disabled={
          props.approveMutation.isPending || props.declineMutation.isPending
        }
        onClick={() => {
          void handleIgnore();
        }}
      >
        Ignore
      </Button>
      <Button
        loading={props.approveMutation.isPending}
        loadingLabel="Approving"
        disabled={
          props.approveMutation.isPending || props.declineMutation.isPending
        }
        onClick={() => {
          void handleApprove();
        }}
      >
        Approve
      </Button>
    </>
  );

  return (
    <Dialog
      open={isOpen}
      title={props.previewMessage?.fromDisplayName ?? "Message request"}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
      footer={footer}
    >
      {body}
    </Dialog>
  );
};
